// §2.11 + SD28: WebSocket progress *and* live meeting transcript client.
// FR-A4 (F14a fixup): `GET /api/v1/ws` authenticates with a single-use, 30-second
// `?ticket=` only. Each (re)connect first calls `POST /api/v1/ws-ticket` through the
// ordinary API client (session cookie in the browser and extension, `Authorization:
// Bearer` in the desktop shell), then opens the socket with the ticket. No access token
// ever goes into the WS URL or a subprotocol. An anonymous visitor gets a 401 from the
// ticket call (or a refused upgrade, which the browser WebSocket API surfaces as a
// generic `close`, indistinguishable from a network blip); we retry with backoff
// either way, which is safe because this channel is push-only [SD6/SD28] and
// idempotent to resubscribe on.
//
// Protocol (exact, binding):
//   client -> server  {"type":"subscribe","grackle_id":"<uuid>"}
//   client -> server  {"type":"live_subscribe","session_id":"<uuid>"}
//   server -> client  {"type":"grackle","grackle_id","status"}
//   server -> client  {"type":"transcript","session_id","line":{...}}
//   server -> client  {"type":"live_status","session_id","status"}
//   server -> client  {"type":"transcript.final","session_id","grackle_id","lines":[...]}
//                     (F17: the post-call F4 transcript replaces the provisional live lines)
//   server -> client  {"type":"notification","notification":{id,kind,payload,created_at,read_at}}
//                     (F7: no subscribe needed — every socket gets its own user's new rows)
// There is no unsubscribe message in §2.11 — a stray push for an id nobody is
// listening to is filtered out by consumers holding no listener for it.

import type { LiveWsMessage } from "../live/types";
import { api, getApiOrigin } from "./client";

export type GrackleWsStatus = "uploading" | "processing" | "ready" | "failed";

export interface GrackleStatusMessage {
  type: "grackle";
  grackle_id: string;
  status: GrackleWsStatus;
}

/** F7: a new in-app notification for the signed-in user (`server/src/ws.rs`). */
export interface WsNotification {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
}

export type NotificationListener = (n: WsNotification) => void;
export type ProgressListener = (msg: GrackleStatusMessage) => void;
export type LiveWsListener = (msg: LiveWsMessage) => void;

/** Exponential backoff with jitter, capped at 20s. Pure — no timers, no globals. */
export function backoffDelayMs(attempt: number, baseMs = 500, capMs = 20_000): number {
  const exp = Math.min(capMs, baseMs * 2 ** Math.max(0, attempt));
  // Jitter in [0.7, 1.3) of the computed delay so many tabs don't reconnect in lockstep.
  return Math.round(exp * (0.7 + Math.random() * 0.6));
}

/** `POST /api/v1/ws-ticket` response (FR-A4): opaque, single use, valid `expires_in` seconds. */
export interface WsTicket {
  ticket: string;
  expires_in: number;
  org_id: string | null;
}

/** Mints a fresh WS ticket with whatever credential the API client already uses. Never log
 * the result: it opens one socket as this user for 30 seconds. */
export async function fetchWsTicket(orgId?: string): Promise<string> {
  const res = await api<WsTicket>("POST", "/ws-ticket", orgId ? { org_id: orgId } : {});
  return res.ticket;
}

export function wsUrl(path: string, ticket: string): string {
  // FR-A4: in the packaged desktop shell `window.location.href` is `tauri://localhost/...`,
  // not the API host — `getApiOrigin()` is what `configureApi` set from `desktop_info`.
  const base = getApiOrigin() || window.location.href;
  const url = new URL(path, base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("ticket", ticket);
  return url.toString();
}

function parseGrackleMessage(parsed: Record<string, unknown>): GrackleStatusMessage | null {
  if (
    parsed.type === "grackle" &&
    typeof parsed.grackle_id === "string" &&
    typeof parsed.status === "string"
  ) {
    return parsed as unknown as GrackleStatusMessage;
  }
  return null;
}

function parseLiveMessage(parsed: Record<string, unknown>): LiveWsMessage | null {
  if (parsed.type === "transcript" && typeof parsed.session_id === "string" && parsed.line) {
    return parsed as unknown as LiveWsMessage;
  }
  if (
    parsed.type === "transcript.final" &&
    typeof parsed.session_id === "string" &&
    Array.isArray(parsed.lines)
  ) {
    return parsed as unknown as LiveWsMessage;
  }
  if (parsed.type === "live_status" && typeof parsed.session_id === "string" && typeof parsed.status === "string") {
    return parsed as unknown as LiveWsMessage;
  }
  return null;
}

/**
 * One `/api/v1/ws` connection, shared by every subscriber. Reconnects on an unexpected close
 * with backoff and replays every still-active subscription once the socket reopens (the
 * server has no session to resume — subscriptions are re-sent, not restored).
 */
export class ProgressSocket {
  private ws: WebSocket | null = null;
  private readonly subscribedIds = new Set<string>();
  private readonly listeners = new Map<string, Set<ProgressListener>>();
  private readonly liveIds = new Set<string>();
  private readonly liveListeners = new Map<string, Set<LiveWsListener>>();
  private readonly notificationListeners = new Set<NotificationListener>();
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = true;
  /** True while a ticket fetch is in flight, so overlapping `connect()` calls (every
   * `subscribe` calls it) don't each mint a ticket and open a socket. */
  private opening = false;

  /** Opens the socket if it isn't already open or opening. Safe to call repeatedly. */
  connect(): void {
    this.closedByUser = false;
    if (this.opening) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    void this.open();
  }

  private async open(): Promise<void> {
    // FR-A4: a browser `WebSocket` cannot set an `Authorization` header on the upgrade
    // request, so every connect (and reconnect) first trades the client's normal credential
    // for a single-use ticket, then passes only that ticket on the URL. A ticket is good for
    // one upgrade, so each reconnect mints a new one.
    this.opening = true;
    let ticket: string;
    try {
      ticket = await fetchWsTicket();
    } catch {
      this.opening = false;
      if (!this.closedByUser) this.scheduleReconnect();
      return;
    }
    this.opening = false;

    if (this.closedByUser) return;

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl("/api/v1/ws", ticket));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      for (const id of this.subscribedIds) this.sendSubscribe(id);
      for (const id of this.liveIds) this.sendLiveSubscribe(id);
    });
    ws.addEventListener("message", (ev) => {
      if (typeof ev.data !== "string") return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (parsed === null || typeof parsed !== "object") return;
      const rec = parsed as Record<string, unknown>;
      if (rec.type === "notification") {
        const n = rec.notification as WsNotification | undefined;
        if (n && typeof n.id === "string" && typeof n.kind === "string") {
          for (const listener of this.notificationListeners) listener(n);
        }
        return;
      }
      const grackle = parseGrackleMessage(rec);
      if (grackle) {
        const forId = this.listeners.get(grackle.grackle_id);
        if (!forId) return;
        for (const listener of forId) listener(grackle);
        return;
      }
      const live = parseLiveMessage(rec);
      if (live) {
        const forId = this.liveListeners.get(live.session_id);
        if (!forId) return;
        for (const listener of forId) listener(live);
      }
    });
    ws.addEventListener("close", () => {
      if (this.ws === ws) this.ws = null;
      if (!this.closedByUser) this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = backoffDelayMs(this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.closedByUser) this.open();
    }, delay);
  }

  private sendSubscribe(grackleId: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "subscribe", grackle_id: grackleId }));
    }
  }

  private sendLiveSubscribe(sessionId: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: "live_subscribe", session_id: sessionId }));
    }
  }

  /**
   * Subscribes to status pushes for one grackle. Connects the socket if needed. Returns an
   * unsubscribe function — call it on unmount. Denial is silent per §2.11 (the server simply
   * never pushes for an id you can't view), so a listener that never fires is expected
   * behavior, not a bug to report.
   */
  subscribe(grackleId: string, listener: ProgressListener): () => void {
    this.connect();
    this.subscribedIds.add(grackleId);
    let set = this.listeners.get(grackleId);
    if (!set) {
      set = new Set();
      this.listeners.set(grackleId, set);
    }
    set.add(listener);
    this.sendSubscribe(grackleId);

    return () => {
      const forId = this.listeners.get(grackleId);
      if (!forId) return;
      forId.delete(listener);
      if (forId.size === 0) {
        this.listeners.delete(grackleId);
        this.subscribedIds.delete(grackleId);
      }
    };
  }

  /** F7: new notifications for the signed-in user. Connects the socket if needed. */
  subscribeNotifications(listener: NotificationListener): () => void {
    this.connect();
    this.notificationListeners.add(listener);
    return () => {
      this.notificationListeners.delete(listener);
    };
  }

  /** SD28: subscribe to live transcript lines for one capture session. */
  subscribeLive(sessionId: string, listener: LiveWsListener): () => void {
    this.connect();
    this.liveIds.add(sessionId);
    let set = this.liveListeners.get(sessionId);
    if (!set) {
      set = new Set();
      this.liveListeners.set(sessionId, set);
    }
    set.add(listener);
    this.sendLiveSubscribe(sessionId);

    return () => {
      const forId = this.liveListeners.get(sessionId);
      if (!forId) return;
      forId.delete(listener);
      if (forId.size === 0) {
        this.liveListeners.delete(sessionId);
        this.liveIds.delete(sessionId);
      }
    };
  }

  /** Tears the socket down and stops reconnecting. Only for whole-app teardown/tests. */
  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }
}

/** One socket for the whole app — WP11's ProcessingPage is the first consumer; a future
 * WatchPage (WP12) subscribing to the same grackle_id shares this connection for free.
 * The Meet overlay (FR-X4) shares it for live transcript (SD28). */
export const progressSocket = new ProgressSocket();
